import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logoMark from "@/assets/logo-mark.png";
import SponsorSlots from "./SponsorSlots";

const SIDEBAR_W = "160px";

const navItems = [
  { label: "Home", href: "/", isLink: true },
  { label: "Services", href: "/services", isLink: true },
  { label: "Venues", href: "/venues", isLink: true },
  { label: "Intelligence", href: "/intelligence", isLink: true },
  { label: "Blog", href: "/blog", isLink: true },
  { label: "FAQs", href: "/faqs", isLink: true },
  { label: "Contact", href: "/contact", isLink: true },
];

const SidebarNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);
    if (!isHomePage) {
      window.location.href = `/#${sectionId}`;
      return;
    }
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const isActive = (href?: string) => {
    if (!href) return false;
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Desktop Top Bar — CTAs only */}
      <header
        className="fixed top-0 right-0 z-50 hidden lg:flex items-center justify-end h-11 px-8"
        style={{ left: SIDEBAR_W }}
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end">
            <span className="font-body text-[7px] uppercase tracking-[0.18em] text-muted-foreground/40 mb-0.5">
              For Venue Partners
            </span>
            <Link
              to="/partner"
              className="font-body text-[9px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              Partner Portal
            </Link>
          </div>
          <Link
            to="/partner/onboarding"
            className="px-4 py-1.5 bg-primary text-primary-foreground font-body text-[9px] uppercase tracking-[0.18em] hover:bg-primary/90 transition-all duration-300"
          >
            Venue Partnership
          </Link>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside
        className="fixed top-0 left-0 bottom-0 z-50 hidden lg:flex flex-col bg-foreground"
        style={{ width: SIDEBAR_W }}
      >
        {/* Logo */}
        <div className="px-4 pt-6 pb-8">
          <Link to="/" className="block">
            <motion.img
              src={logoMark}
              alt="Venue Vine"
              className="h-9 brightness-0 invert opacity-90"
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            />
          </Link>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 flex flex-col px-4 gap-px">
          {navItems.map((item) => {
            const active = isActive(item.href);
            const baseClasses =
              "group relative py-2 text-left font-body text-[9px] uppercase tracking-[0.2em] transition-all duration-300";
            const colorClasses = active
              ? "text-primary-foreground"
              : "text-primary-foreground/40 hover:text-primary-foreground/75";

            const inner = (
              <>
                {active && (
                  <motion.span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-2.5 bg-primary-foreground/50 rounded-full"
                    layoutId="nav-active"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                <span className={active ? "ml-2.5" : "ml-0 group-hover:ml-1 transition-all duration-200"}>
                  {item.label}
                </span>
              </>
            );

            return (
              <Link key={item.label} to={item.href!} className={`${baseClasses} ${colorClasses}`}>
                {inner}
              </Link>
            );
          })}
        </nav>

        {/* Strategic Partners */}
        <div className="px-4 pb-3">
          <div className="border-t border-primary-foreground/8 pt-3 mb-2">
            <p className="font-body text-[7px] uppercase tracking-[0.2em] text-primary-foreground/25 mb-1.5">
              For Venue Partners
            </p>
            <SponsorSlots variant="sidebar" />
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="px-4 pb-5">
          <p className="font-body text-[7px] uppercase tracking-[0.15em] text-primary-foreground/15">
            © {new Date().getFullYear()} Venue Vine
          </p>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 lg:hidden flex items-center justify-between h-14 px-6 bg-background/95 backdrop-blur-sm border-b border-border/20">
        <Link to="/" className="flex items-center">
          <img src={logoMark} alt="Venue Vine" className="h-8" />
        </Link>

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <button className="p-2 text-foreground" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-full sm:w-72 bg-background border-l border-border/30"
          >
            <div className="flex flex-col h-full pt-10">
              <div className="flex flex-col">
                {navItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.href!}
                      onClick={() => setIsOpen(false)}
                      className="py-3.5 px-2 text-left font-body text-xs uppercase tracking-[0.15em] text-foreground hover:text-primary transition-colors border-b border-border/10"
                    >
                      {item.label}
                    </Link>
                ))}
              </div>

              <div className="mt-auto pb-8 flex flex-col gap-3">
                <Link
                  to="/partner"
                  onClick={() => setIsOpen(false)}
                  className="py-3 text-center font-body text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors"
                >
                  Partner Portal
                </Link>
                <Link
                  to="/partner/onboarding"
                  onClick={() => setIsOpen(false)}
                  className="py-3 text-center bg-primary text-primary-foreground font-body text-[10px] uppercase tracking-[0.15em] hover:bg-primary/90 transition-colors"
                >
                  Venue Partnership
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </header>
    </>
  );
};

export default SidebarNav;
